import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { useNotifications } from '../../context/NotificationContext';
import { useSubscription } from '../../hooks/useSubscription';
import DotSpinner from '../../components/shared/DotSpinner';
import { NotificationCard } from '../../components/notifications/notificationCard';
import { calculateAge } from '../../utils/calculateAge';
import { sortSympathyNotifications } from '../../utils/sympathyOrder';

// Заблюреная строка для лайков/суперлайков без подписки: клик ведёт на пейвол
function LockedRow({ notification }) {
  const navigate = useNavigate();
  const isNew = !notification?.seen;
  const isSuperlike = notification?.type === 'superlike';
  const payload = notification?.payload || {};
  const userPhoto = payload?.photo;

  return (
    <div
      className="text-white w-full flex flex-row h-[84px] items-center font-raleway cursor-pointer hover:bg-[#FFFFFF0A] transition-colors"
      onClick={() => navigate(config.ROUTES.PREMIUM)}
    >
      {userPhoto ? (
        <img
          src={userPhoto}
          alt=""
          className="w-[48px] h-[48px] rounded-[50%] ml-[16px] mr-[16px] object-cover shrink-0 blur-[8px] scale-[1.05]"
        />
      ) : (
        <div className="w-[48px] h-[48px] rounded-[50%] ml-[16px] mr-[16px] bg-[#233636] text-white flex items-center justify-center text-[20px] shrink-0">
          ?
        </div>
      )}
      <div className="flex flex-col w-[220px] gap-1">
        <p className="text-[14px] font-normal">{isSuperlike ? 'Вам поставили СУПЕРЛАЙК!' : 'Вам поставили лайк!'}</p>
        <p className="text-[16px] font-semibold leading-[24px]">Скрыто</p>
      </div>
      <div className="ml-auto flex flex-col justify-center w-[100px] items-end mr-3">
        {isNew && (
          <div className="rounded-[400px] bg-[#A1F69E] w-[58px] h-[25px] flex items-center justify-center text-black font-semibold text-[12px]">
            NEW
          </div>
        )}
        {isSuperlike && (
          <img src="/icons/bg-superlike.svg" alt="Суперлайк" className="absolute w-[84px] h-[84px] right-0" />
        )}
      </div>
    </div>
  );
}

// Список симпатий/лайков/суперлайков строками-карточками.
// Открывается по кнопке «Все» из ленты симпатий над чатами.
// Взаимные — первыми. Без подписки: взаимные видны и кликабельны,
// лайки/суперлайки — под блюром, клик ведёт на пейвол.
export function Sympathies() {
  const axiosPrivate = useAxiosPrivate();
  const { isActiveSubscription } = useSubscription();
  const { updateCounts } = useNotifications();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);
      try {
        const response = await axiosPrivate.get(config.API.NOTIFICATIONS.LIST, {
          // СИМПАТИИ = только супер-лайки и взаимные симпатии;
          // обычные лайки живут отдельно на странице /likes
          params: { type: 'mutual,superlike', limit: 50 }
        });

        if (response.data?.status === 'success') {
          setNotifications(response.data.data.items || []);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    loadNotifications();
  }, []);

  // Порядок: взаимные → суперлайки → лайки, внутри групп новые первыми
  const sorted = useMemo(() => sortSympathyNotifications(notifications), [notifications]);

  const refreshCounts = async () => {
    try {
      const response = await axiosPrivate.get(config.API.NOTIFICATIONS.COUNTS);
      if (response.data?.status === 'success') {
        updateCounts(response.data.data);
      }
    } catch (err) {
      console.error('Error refreshing counts:', err);
    }
  };

  // Клик по карточке = просмотр: помечаем прочитанными все уведомления этого юзера
  const markUserAsRead = async (notification) => {
    const payloadUserId = notification?.payload?._id;
    if (!payloadUserId) return;

    const targets = notifications.filter((n) => !n.seen && n.payload?._id === payloadUserId);
    if (targets.length === 0) return;

    try {
      await Promise.all(
        targets.map((n) =>
          axiosPrivate.post(config.API.NOTIFICATIONS.MARK_READ(n._id)).catch((err) => console.error(err))
        )
      );
      setNotifications((prev) => prev.map((n) => (n.payload?._id === payloadUserId ? { ...n, seen: true } : n)));
      refreshCounts();
    } catch (error) {
      console.error('Error marking user notifications as read:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh] [background:linear-gradient(180deg,rgb(1,13,13)_0%,rgb(3.01,42.5,42.5)_100%)] bg-[100%_100%] bg-repeat-y bg-cover">
        <DotSpinner />
      </div>
    );
  }

  return (
    <div className="w-full pb-[80px] flex flex-col items-center">
      <div className="mt-[16px] w-full">
        {loading ? (
          <DotSpinner />
        ) : sorted.length > 0 ? (
          sorted.map((notification) => {
            // Без подписки: взаимные — открыто, лайки/суперлайки — под блюром с пейволом
            if (!isActiveSubscription && notification.type !== 'mutual') {
              return (
                <div key={notification._id} className="border-b border-[#233636] w-full">
                  <LockedRow notification={notification} />
                </div>
              );
            }

            return (
              <div
                key={notification._id}
                className="border-b border-[#233636] w-full"
                onClick={() => markUserAsRead(notification)}
              >
                <NotificationCard notification={notification} onRead={() => {}} option={1} />
              </div>
            );
          })
        ) : (
          <div className="flex flex-col justify-center items-center h-[400px] text-white text-center px-4 gap-7">
            <img src="/icons/heart-w.svg" className="size-[48px] opacity-60" />
            <div>
              <h4 className="text-[24px] font-raleway font-medium">Пока пусто</h4>
              <p>Здесь появятся ваши симпатии и лайки</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Sympathies;
